import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractRow from "../../../../models/reports/reports/rows/AbstractRow.js";
import FormulaRow from "../../../../models/reports/reports/rows/FormulaRow.js";
import ReportVisitor from "../../../../models/reports/reports/ReportVisitor.js";

/**
 * Visitor this renders a single row in a PDF export.
 *
 * @param {report} spec.report - Report to be visited
 * @param {object} spec.definition - Initial PDFMake definition
 */
export default class PdfRowRenderer extends ReportVisitor {
	constructor({ report, definition }) {
		super(...arguments);
		this._report = report;
		this._definition = definition;
	}

	visitAbstractRow(row) {
		if (!this._report.isRowVisible(row)) {
			return;
		}

		const result = [row.getFullTitleString()];

		result.push(
			...this._visitAllVisibleColumns().map((renderer) => renderer(row)),
		);

		this._define(result);
	}

	visitSpacerRow() {
		const result = [
			{
				text: " ",
				border: [false, false, false, false],
			},
		];

		result.push(
			...this._report.getVisibleColumns().map(() => ({
				text: "",
				border: [false, false, false, false],
			})),
		);

		this._define(result);
	}

	visitAccountRow(row) {
		if (!this._report.isRowVisible(row)) {
			return;
		}

		const result = [
			{
				text: row.getFullTitleString(),
				style: "accountLabel",
				margin: this._getRowMargin(row),
				noWrap: true,
			},
		];

		const values = this._visitAllVisibleColumns()
			.map((renderer) => renderer(row))
			.map((value) => {
				value.style = "accountDataCell";
				value.noWrap = true;
				return value;
			});

		result.push(...values);

		this._define(result);
	}

	visitGroupRow(groupRow) {
		const groupHasSummaryRow = this._report.hasSummaryRow({
			groupRow,
		});

		const titleRow = [
			{
				text: groupRow.getFullTitleString(),
				style: "tableSubheader",
				margin: this._getRowMargin(groupRow),
				noWrap: true,
			},
		];

		titleRow.push(
			...this._visitAllVisibleColumns().map((renderer) => ({
				text:
					groupRow.isExpanded() && groupHasSummaryRow
						? ""
						: renderer(groupRow),
				style: "tableSubheader",
				noWrap: true,
			})),
		);

		this._define(titleRow);

		if (groupRow.isExpanded()) {
			this.visitAll(this._report.getVisibleChildren({ row: groupRow }));
			if (groupHasSummaryRow) {
				this._define(this._getGroupSummary(groupRow));
			}
		} else {
			this.visitAll(
				groupRow
					.getAllLeafRows()
					.filter((row) => this._report.isRowVisible(row)),
			);
		}
	}

	visitRepeatingRow(group) {
		this.visitAll(this._report.getVisibleChildren({ row: group }));
	}

	visitFormulaRow(row) {
		const result = [
			{
				text: row.getFullTitleString(),
				style: `row_${row.getStyle()}`,
				margin: this._getRowMargin(row),
				noWrap: true,
			},
		];

		result.push(
			...this._visitAllVisibleColumns().map((renderer) => renderer(row)),
		);

		this._define(result);
	}

	// eslint-disable-next-line sonarjs/no-identical-functions -- we should fix that
	visitKeyFigureRow(row) {
		const result = [
			{
				text: row.getFullTitleString(),
				style: `row_${row.getStyle()}`,
				margin: this._getRowMargin(row),
				noWrap: true,
			},
		];

		result.push(
			...this._visitAllVisibleColumns().map((renderer) => renderer(row)),
		);

		this._define(result);
	}

	visitTextRow(row) {
		const result = [
			{
				text: row.getFullTitleString(),
				style: `row_${row.getStyle()}`,
				margin: this._getRowMargin(row),
				noWrap: true,
			},
		];
		result.push(...this._report.getVisibleColumns().map(() => ""));
		this._define(result);
	}

	visitAbstractColumn(column) {
		return (row) => ({
			text: this._report.getFormattedData({ column, row }),
			style: row.getPDFDataStyle(),
		});
	}

	_visitAllVisibleColumns() {
		return this.visitAll(this._report.getVisibleColumns());
	}

	_getGroupSummary(group) {
		const row = [
			{
				text: _("Sum {title}", {
					title: group.getFullTitleString(),
				}).toString(),
				style: "tableSum",
				margin: this._getRowMargin(group),
				noWrap: true,
			},
		];

		row.push(
			...this._visitAllVisibleColumns().map((renderer) =>
				renderer(group),
			),
		);

		return row;
	}

	_define(content) {
		this._definition.table.body.push(content);
	}

	_getRowMargin(row) {
		return [this._getLeftMargin(row), 0, 0, 0];
	}

	_getLeftMargin(row) {
		const leftMarginByDepth = 10;
		return (row.getVisibilityDepth() - 1) * leftMarginByDepth;
	}
}

AbstractRow.prototype.getPDFDataStyle = function () {
	return "tableData";
};

FormulaRow.prototype.getPDFDataStyle = function () {
	return "tableFormulaData";
};
